/**
 * Cuando volver a ofrecer la instalacion.
 *
 * "Ahora no" se respeta: la siguiente vez que se ofrece sola es a los tres
 * dias, luego a los siete, luego a los catorce, y despues de tres "ahora no" ya
 * no se ofrece sola nunca —queda en Configuracion, para quien la busque—. Un
 * cartel que vuelve en cada apertura enseña a cerrarlo sin leerlo.
 *
 * Vive en el aparato (`localStorage`) porque la instalacion es del aparato:
 * instalarla en el telefono no la instala en la computadora del mostrador. Y es
 * una comodidad por persona y aparato, que es justo lo que `localStorage` puede
 * guardar sin prometer nada mas (si se borra, se vuelve a ofrecer y ya).
 */

export interface InstallReminder {
  /** Cuantas veces se ha dicho "ahora no". */
  times: number;
  /** Hasta cuando no se ofrece sola (milisegundos), o `null` si ya se puede. */
  until: number | null;
}

/** El prefijo `abarrotes.` es el de todas las llaves del sistema (CLAUDE.md §1). */
export const INSTALL_REMINDER_KEY = 'abarrotes.install';

const DAY_MS = 86_400_000;

/** Los dias de espera tras el primer, el segundo y el tercer "ahora no". */
export const WAIT_DAYS = [3, 7, 14] as const;

export const NO_ANSWER: InstallReminder = { times: 0, until: null };

export function shouldOffer(r: InstallReminder, now: number): boolean {
  if (r.times >= WAIT_DAYS.length) return false;
  return r.until === null || now >= r.until;
}

export function afterNotNow(r: InstallReminder, now: number): InstallReminder {
  const times = r.times + 1;
  const days = WAIT_DAYS[Math.min(times, WAIT_DAYS.length) - 1] ?? 0;
  return { times, until: now + days * DAY_MS };
}

/** Lo guardado, o "sin respuesta" si no hay nada o no se entiende. */
export function readInstallReminder(): InstallReminder {
  try {
    const raw = localStorage.getItem(INSTALL_REMINDER_KEY);
    if (!raw) return NO_ANSWER;
    const r = JSON.parse(raw) as Partial<InstallReminder> | null;
    return r !== null &&
      typeof r.times === 'number' &&
      Number.isInteger(r.times) &&
      r.times >= 0 &&
      (r.until === null || typeof r.until === 'number')
      ? { times: r.times, until: r.until }
      : NO_ANSWER;
  } catch {
    return NO_ANSWER;
  }
}

export function saveInstallReminder(r: InstallReminder): void {
  try {
    localStorage.setItem(INSTALL_REMINDER_KEY, JSON.stringify(r));
  } catch {
    // Sin almacenamiento —modo privado— se vuelve a ofrecer la proxima vez.
  }
}
